import React, { useEffect, useState } from 'react';
import { Alert, Badge, Col, Form, ListGroup, Row } from 'react-bootstrap';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import emailPreviewService from '@/shared/services/emailPreviewService';
import './EmailPreviewsTab.css';

// The member-facing names of the two switches, matching the profile page.
const PREFERENCE_LABELS = {
    weeklyDigest: 'Weekly digest of new listings',
    expiryReminders: 'Reminders before my listings expire',
};

// Who an email reaches, in the words the rest of the site uses.
const ROLE_LABELS = {
    owner: 'The poster',
    requester: 'The requester',
    facilities: 'Facilities offices',
    member: 'Subscribed members',
};

const roleLabel = (role) => ROLE_LABELS[role] || role;

function RequiredBadge({ entry }) {
    return entry.required ? (
        <Badge bg="dark">Required</Badge>
    ) : (
        <Badge bg="secondary">Optional</Badge>
    );
}

// Which profile switch turns an optional email off, on a line of its own so
// it never squeezes the email's name.
function PreferenceNote({ entry }) {
    if (entry.required || !entry.preference) return null;
    return (
        <span className="d-block small afe-email-when">
            Profile switch:{' '}
            {PREFERENCE_LABELS[entry.preference] || entry.preference}
        </span>
    );
}

/**
 * Every email the exchange sends, as its recipient would receive it, built
 * from the catalog's sample data. This is how a stakeholder reviews wording
 * before members see it. Nothing is sent from this tab.
 *
 * The HTML part renders in an iframe with an empty sandbox, so nothing in a
 * message (a script, a form, a link that navigates) can act on the admin's
 * session; the server escapes every value as well.
 */
function EmailPreviewsTab() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [listError, setListError] = useState('');
    const [eventKey, setEventKey] = useState('');
    const [role, setRole] = useState('');
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState('');

    useEffect(() => {
        let active = true;
        emailPreviewService
            .listEvents()
            .then((data) => {
                if (active) setEvents(data.events || []);
            })
            .catch(() => {
                if (active) setListError('Could not load the email list.');
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        if (!eventKey || !role) return undefined;
        let active = true;
        setPreview(null);
        setPreviewError('');
        emailPreviewService
            .getPreview(eventKey, role)
            .then((data) => {
                if (active) setPreview(data);
            })
            .catch(() => {
                if (active) setPreviewError('Could not render that email.');
            });
        return () => {
            active = false;
        };
    }, [eventKey, role]);

    const select = (entry) => {
        setEventKey(entry.key);
        setRole(entry.roles[0]);
    };

    if (loading) {
        return <LoadingSpinner message="Loading the email list" />;
    }

    const selected = events.find((entry) => entry.key === eventKey);

    return (
        <section aria-labelledby="email-previews-heading">
            <h2 id="email-previews-heading" className="h4 mb-3">
                Email previews
            </h2>
            <p className="text-muted">
                Each email the exchange sends, built from sample data. Members
                can turn off only the optional ones, on their profile. Nothing
                is sent from this page.
            </p>
            {listError && <Alert variant="danger">{listError}</Alert>}
            <Row className="g-4">
                <Col lg={5}>
                    <ListGroup>
                        {events.map((entry) => (
                            <ListGroup.Item
                                key={entry.key}
                                action
                                active={entry.key === eventKey}
                                aria-current={
                                    entry.key === eventKey ? 'true' : undefined
                                }
                                onClick={() => select(entry)}
                                className="afe-email-entry"
                            >
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                    <span className="fw-semibold">
                                        {entry.label || entry.key}
                                    </span>
                                    <RequiredBadge entry={entry} />
                                </div>
                                {entry.when && (
                                    <span className="d-block small afe-email-when">
                                        {entry.when}
                                    </span>
                                )}
                                <span className="d-block small afe-email-when">
                                    To: {entry.roles.map(roleLabel).join(', ')}
                                </span>
                                <PreferenceNote entry={entry} />
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </Col>
                <Col lg={7} className="afe-email-detail">
                    {!selected && events.length > 0 && (
                        <p className="text-muted border rounded p-3">
                            Choose an email to see it as its recipient would.
                        </p>
                    )}
                    {selected && (
                        <section aria-labelledby="email-preview-heading">
                            <h3 id="email-preview-heading" className="h5">
                                {selected.label || selected.key}
                            </h3>
                            {selected.roles.length > 1 && (
                                <Form.Group
                                    controlId="email-preview-role"
                                    className="mb-3 afe-email-preview-role"
                                >
                                    <Form.Label>
                                        Show it as received by
                                    </Form.Label>
                                    <Form.Select
                                        value={role}
                                        onChange={(event) =>
                                            setRole(event.target.value)
                                        }
                                    >
                                        {selected.roles.map((name) => (
                                            <option key={name} value={name}>
                                                {roleLabel(name)}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            )}
                            {previewError && (
                                <Alert variant="danger">{previewError}</Alert>
                            )}
                            {!preview && !previewError && (
                                <LoadingSpinner message="Rendering the email" />
                            )}
                            {preview && (
                                <>
                                    <h4 className="h6">Subject</h4>
                                    <p>{preview.subject}</p>
                                    {preview.replyTo &&
                                        preview.replyTo.length > 0 && (
                                            <p className="small text-muted">
                                                Replies go to:{' '}
                                                {preview.replyTo.join(', ')}
                                            </p>
                                        )}
                                    <h4 className="h6">HTML version</h4>
                                    <iframe
                                        title={`HTML version of ${selected.label || preview.event} for ${roleLabel(preview.role).toLowerCase()}`}
                                        sandbox=""
                                        srcDoc={preview.html}
                                        className="afe-email-preview-frame w-100 border rounded mb-3"
                                    />
                                    <h4 className="h6">Plain-text version</h4>
                                    <pre className="afe-email-preview-text border rounded p-3 bg-light">
                                        {preview.text}
                                    </pre>
                                </>
                            )}
                        </section>
                    )}
                </Col>
            </Row>
        </section>
    );
}

export default EmailPreviewsTab;
